import localforage from 'localforage';
import type { Persistor } from 'redux-persist';
import { clearPortalLocalStorage } from '@rsos/base-utils/clearPortalLocalStorage';
import NS_SSO from '@rsos/constants/ns_sso';
import { logout } from '@rsos/sinatra';
import { captureException } from '@rsos/utils/sentry';

/** Only the parts driven here, so apps that don't persist Redux state pass nothing. */
type LogoutPersistor = Pick<Persistor, 'pause' | 'flush'>;

/** Takes the logout thunk itself, which any real store dispatch satisfies. */
type LogoutDispatch = (action: ReturnType<typeof logout>) => Promise<void>;

/**
 * Cleans the store and redirects.
 */
const onLogout = async (
  persistor: LogoutPersistor | undefined,
  dispatch: LogoutDispatch,
) => {
  let redirectPath = '/';

  // NS sessions are tagged with ns_sso in localStorage. This will only be
  // present if the user knows of the /ns path, see the NSLogin component.
  if (localStorage.getItem(NS_SSO)) {
    redirectPath = '/ns';
  }

  try {
    // pause() stops new writes, flush() awaits the one already in flight.
    persistor?.pause();
    await dispatch(logout());
    await persistor?.flush();
    clearPortalLocalStorage();
    await localforage.clear();

    // clear() empties the store; this drops the database itself and is best effort,
    // since another tab's open connection blocks it.
    if (window.indexedDB) {
      window.indexedDB.deleteDatabase('localforage');
    }
  } catch (error) {
    captureException(error instanceof Error ? error : String(error));
  } finally {
    // Logout and redirect no matter what happens.
    // Assigning a string to window.location is the legacy navigation setter.
    (window as unknown as { location: string }).location = redirectPath;
  }
};

export default onLogout;
